import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { AlertTriangle, Banknote, Download, Landmark, RefreshCw } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { inr, inrFull, localDate, localDateOffset } from '@/lib/format'

import {
  downloadSettlementXlsx,
  useImportSettlements,
  useResolveException,
  useSettlementExceptions,
  useSettlementLines,
  useSettlements,
  type DateRange,
  type ExceptionRow,
  type SettlementRow,
} from './api'

/** What each finding means, in the words a finance person would use. */
const KIND_LABEL: Record<string, string> = {
  UNKNOWN_AT_LEDGER: 'Settled, but we have no payment for it',
  AMOUNT_MISMATCH: 'Settled for a different amount',
  STATUS_DRIFT: 'Settled against a payment we recorded as failed',
  UNSETTLED_AGING: 'Collected, never settled',
}

function today(): string {
  return localDate()
}

function daysAgo(n: number): string {
  return localDateOffset(-n)
}

function errText(err: unknown): string {
  return isAxiosError(err)
    ? ((err.response?.data as { message?: string })?.message ?? err.message)
    : String(err)
}

/**
 * Settlements — has the money the gateway collected actually reached the bank?
 *
 * Distinct from the Reconciliation screen, which reconciles our case state
 * against the lender's LMS. This one reconciles our payment ledger against the
 * provider's settlement report, and the two answer different questions about
 * different systems.
 */
export function SettlementsPage() {
  const [from, setFrom] = useState(daysAgo(30))
  const [to, setTo] = useState(today())
  const [openBatch, setOpenBatch] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ title: string; body: string } | null>(null)

  const range: DateRange = useMemo(() => ({ from, to }), [from, to])

  const batches = useSettlements(range)
  const exceptions = useSettlementExceptions('OPEN')
  const importer = useImportSettlements()

  const netCredited = (batches.data ?? []).reduce((sum, b) => sum + b.netAmount, 0)
  const unmatchedLines = (batches.data ?? []).reduce(
    (sum, b) => sum + (b.lineCount - b.matchedCount),
    0,
  )

  async function onImport() {
    setBanner(null)
    try {
      const r = await importer.mutateAsync(range)
      setBanner({
        title: 'Report imported',
        body:
          `${r.settlementsImported} new batch(es), ${r.alreadyKnown} already known, ` +
          `${r.linesMatched} line(s) matched, ${r.exceptionsRaised} exception(s) raised, ` +
          `${r.agingFlagged} collection(s) flagged as unsettled.`,
      })
    } catch (err) {
      setBanner({ title: 'Could not import the report', body: errText(err) })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-navy-900">Settlements</h1>
          <p className="text-xs text-slate-500">
            What the gateway credited to the bank, and what does not add up.
          </p>
        </div>
        <Button onClick={onImport} disabled={importer.isPending}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          {importer.isPending ? 'Importing…' : 'Import report'}
        </Button>
      </div>

      {banner && (
        <Alert>
          <AlertTitle>{banner.title}</AlertTitle>
          <AlertDescription>{banner.body}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <div>
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Net credited"
          value={inr(netCredited)}
          hint="what reached the bank in this range"
          loading={batches.isLoading}
          icon={<Landmark className="h-3.5 w-3.5" />}
        />
        <Kpi
          label="Batches"
          value={String(batches.data?.length ?? 0)}
          loading={batches.isLoading}
          icon={<Banknote className="h-3.5 w-3.5" />}
        />
        <Kpi
          label="Open exceptions"
          value={String(exceptions.data?.length ?? 0)}
          hint="ledger and gateway disagree"
          loading={exceptions.isLoading}
          deltaSense="neutral"
          icon={<AlertTriangle className="h-3.5 w-3.5" />}
        />
        <Kpi
          label="Unmatched lines"
          value={String(unmatchedLines)}
          hint="settled but not cleanly placed"
          loading={batches.isLoading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Settlement batches</CardTitle>
          <CardDescription>
            One row per bank credit. Open a batch to see the collections it paid for.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {batches.isLoading ? (
            <SkeletonRows rows={5} />
          ) : batches.data && batches.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              No settlements in this range. Import the provider&apos;s report to pull them in.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Settled on</th>
                    <th className="py-2 pr-3">Bank UTR</th>
                    <th className="py-2 pr-3 text-right">Gross</th>
                    <th className="py-2 pr-3 text-right">Fees + tax</th>
                    <th className="py-2 pr-3 text-right">Net</th>
                    <th className="py-2 pr-3 text-right">Lines</th>
                    <th className="py-2 pr-3">Health</th>
                    <th className="py-2 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {batches.data?.map((b) => (
                    <BatchRows
                      key={b.settlementId}
                      batch={b}
                      open={openBatch === b.settlementId}
                      onToggle={() =>
                        setOpenBatch(openBatch === b.settlementId ? null : b.settlementId)
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Open exceptions</CardTitle>
          <CardDescription>
            Money whose location the ledger and the gateway describe differently. Nothing here is
            repaired automatically — each one is a decision. Not filtered by the dates above: an
            exception raised last month is still open money.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {exceptions.isLoading ? (
            <SkeletonRows rows={4} />
          ) : exceptions.data && exceptions.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              Nothing outstanding — every settled line is placed.
            </p>
          ) : (
            <div className="space-y-2">
              {exceptions.data?.map((e) => (
                <ExceptionCard key={e.exceptionId} row={e} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function BatchRows({
  batch,
  open,
  onToggle,
}: {
  batch: SettlementRow
  open: boolean
  onToggle: () => void
}) {
  const lines = useSettlementLines(open ? batch.settlementId : null)
  const [downloading, setDownloading] = useState(false)
  const clean = batch.exceptionCount === 0 && batch.matchedCount === batch.lineCount

  async function onDownload() {
    setDownloading(true)
    try {
      await downloadSettlementXlsx(batch)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <tr className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={onToggle}>
        <td className="py-1.5 pr-3 tabular-nums">{batch.settledOn}</td>
        <td className="py-1.5 pr-3 font-mono text-xs">{batch.utr ?? '—'}</td>
        <td className="py-1.5 pr-3 text-right tabular-nums">{inrFull(batch.grossAmount)}</td>
        <td className="py-1.5 pr-3 text-right tabular-nums">
          {inrFull(batch.feeAmount + batch.taxAmount)}
        </td>
        <td className="py-1.5 pr-3 text-right font-medium tabular-nums">
          {inrFull(batch.netAmount)}
        </td>
        <td className="py-1.5 pr-3 text-right tabular-nums">
          {batch.matchedCount}/{batch.lineCount}
        </td>
        <td className="py-1.5 pr-3">
          <Badge>
            {clean
              ? 'RECONCILED'
              : `${batch.exceptionCount} ${batch.exceptionCount === 1 ? 'EXCEPTION' : 'EXCEPTIONS'}`}
          </Badge>
        </td>
        <td className="py-1.5 pr-3 text-right">
          <Button
            variant="ghost"
            onClick={(ev) => {
              ev.stopPropagation()
              void onDownload()
            }}
            disabled={downloading}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-slate-100 bg-slate-50/60">
          <td colSpan={8} className="px-3 py-2">
            {lines.isLoading ? (
              <SkeletonRows rows={3} />
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase tracking-wider text-slate-500">
                    <th className="py-1 pr-3">Payment</th>
                    <th className="py-1 pr-3">Loan</th>
                    <th className="py-1 pr-3">Customer</th>
                    <th className="py-1 pr-3 text-right">Amount</th>
                    <th className="py-1 pr-3">Paid</th>
                    <th className="py-1 pr-3">Status</th>
                    <th className="py-1 pr-3">Collected by</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.data?.map((l) => (
                    <tr key={l.paymentId}>
                      <td className="py-1 pr-3 font-mono">{l.paymentRef}</td>
                      <td className="py-1 pr-3 font-mono">{l.loanAccountNo}</td>
                      <td className="py-1 pr-3">{l.customerName ?? '—'}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{inrFull(l.amount)}</td>
                      <td className="py-1 pr-3 tabular-nums">{l.paidAt?.slice(0, 10) ?? '—'}</td>
                      <td className="py-1 pr-3">{l.status}</td>
                      <td className="py-1 pr-3">
                        {l.empId ? `${l.empId} · ${l.agentName ?? ''}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function ExceptionCard({ row }: { row: ExceptionRow }) {
  const [note, setNote] = useState('')
  const [status, setStatus] = useState('RESOLVED')
  const [error, setError] = useState<string | null>(null)
  const resolve = useResolveException()

  async function onResolve() {
    setError(null)
    try {
      await resolve.mutateAsync({ exceptionId: row.exceptionId, status, note })
    } catch (err) {
      setError(errText(err))
    }
  }

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Badge>{row.kind}</Badge>
            <span className="text-sm font-medium text-navy-900">
              {KIND_LABEL[row.kind] ?? row.kind}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">{row.detail}</p>
          <p className="mt-1 font-mono text-2xs text-slate-500">
            {row.paymentRef ?? row.gatewayOrderId ?? '—'}
            {row.loanAccountNo ? ` · ${row.loanAccountNo}` : ''}
            {row.utr ? ` · UTR ${row.utr}` : ''}
          </p>
        </div>
        <div className="text-right text-xs tabular-nums">
          {row.gatewayAmount !== null && (
            <div>
              <span className="text-slate-500">gateway </span>
              {inrFull(row.gatewayAmount)}
            </div>
          )}
          {row.ledgerAmount !== null && (
            <div>
              <span className="text-slate-500">ledger </span>
              {inrFull(row.ledgerAmount)}
            </div>
          )}
          <div className="text-slate-400">{row.detectedAt.slice(0, 10)}</div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
        >
          <option value="RESOLVED">Resolved — found the money</option>
          <option value="WRITTEN_OFF">Written off — accepted the loss</option>
        </select>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What did you find? (required)"
          className="min-w-[16rem] flex-1"
        />
        <Button onClick={onResolve} disabled={resolve.isPending || note.trim().length === 0}>
          {resolve.isPending ? 'Closing…' : 'Close'}
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  )
}
